/**
 * EditSuggestionPreview Component
 * Feature: 043-critique-edit-suggestions
 *
 * Displays a thumbnail preview of an edit suggestion attached to a critique comment.
 * Clicking the thumbnail opens a modal dialog with a full-size comparison view.
 *
 * T063-T067: Thumbnail + modal preview implementation
 */

import { useState, useEffect, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { EditPreviewRenderer, EditPreviewComparison } from '../EditPreview/EditPreview';
import type { CritiqueEditOrPartial, CombinedEditData, CropData } from '../../../types/edit.types';
import type { Photo } from '../../../types/photo';
import { getVariantUrl, selectVariant, type VariantType } from '../../photos/AdaptivePhoto';

// Constants for layout calculations
const RESIZE_DEBOUNCE_MS = 150;
const COMPARISON_GRID_GAP_PX = 16; // Tailwind gap-4 (4 * 4px)
const COMPARISON_COLUMNS = 2;

/**
 * Debounce function to limit how often a function is called
 */
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}

export interface EditSuggestionPreviewProps {
  /** Photo being edited */
  photo: Photo;
  /** Edit suggestion data (full CritiqueEdit or partial with just editType and editData) */
  edit: CritiqueEditOrPartial;
  /** Loading state (when fetching edit data) */
  loading?: boolean;
  /** Feature 652: Callback when user clicks edit button */
  onEdit?: () => void;
  /** Feature 652: Callback when user clicks remove button */
  onRemove?: () => void;
}

/**
 * Displays edit suggestion as thumbnail with click-to-expand modal
 *
 * @example
 * <EditSuggestionPreview
 *   photo={photo}
 *   edit={critique.edit}
 * />
 */
export function EditSuggestionPreview({
  photo,
  edit,
  loading = false,
  onEdit,
  onRemove,
}: EditSuggestionPreviewProps): JSX.Element {

  // T065: Modal open state
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Track selected variant for modal display
  const [modalVariant, setModalVariant] = useState<VariantType>('small');
  // Store container element for ResizeObserver
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);

  // Memoized helper function to calculate variant from container size
  // Depends on edit to access crop data for resolution scaling
  const calculateVariant = useCallback((width: number, height: number): VariantType => {
    // Comparison has 2 columns with gap, so each image gets roughly half the width
    const imageWidth = (width - COMPARISON_GRID_GAP_PX) / COMPARISON_COLUMNS;

    // If there's a crop, we need higher resolution to maintain quality in the cropped area
    // Crop dimensions are stored as percentages (0-100), so we scale up by the inverse
    let effectiveWidth = imageWidth;
    let effectiveHeight = height;

    if (edit.editType === 'crop' || edit.editType === 'combined') {
      let cropData: CropData | undefined;

      if (edit.editType === 'crop') {
        cropData = edit.editData as CropData;
      } else if (edit.editType === 'combined') {
        cropData = (edit.editData as CombinedEditData).crop;
      }

      if (cropData && typeof cropData.width === 'number' && typeof cropData.height === 'number') {
        // Crop dimensions are stored as percentages (0-100), convert to decimal (0-1)
        const cropWidthDecimal = cropData.width / 100;
        const cropHeightDecimal = cropData.height / 100;

        // Scale up by inverse of crop percentage to get required source resolution
        effectiveWidth = imageWidth / cropWidthDecimal;
        effectiveHeight = height / cropHeightDecimal;
      }
    }

    return selectVariant(effectiveWidth, effectiveHeight);
  }, [edit]);

  // Callback ref that measures when the element is actually attached
  const handleRefAttached = useCallback((element: HTMLDivElement | null) => {
    setContainerElement(element);

    if (element && isModalOpen) {
      const { width, height } = element.getBoundingClientRect();
      const variant = calculateVariant(width, height);
      setModalVariant(variant);
    }
  }, [isModalOpen, calculateVariant]);

  // Observe container size changes (asynchronous, responsive)
  useEffect(() => {
    if (!isModalOpen || !containerElement) return;

    // Debounced resize handler
    const handleResize = debounce((width: number, height: number) => {
      const newVariant = calculateVariant(width, height);

      // Only update if variant actually changed (prevents unnecessary image reloads)
      setModalVariant(currentVariant => {
        if (newVariant !== currentVariant) {
          return newVariant;
        }
        return currentVariant;
      });
    }, RESIZE_DEBOUNCE_MS);

    // Create ResizeObserver
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        handleResize(width, height);
      }
    });

    observer.observe(containerElement);

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, [isModalOpen, containerElement, calculateVariant]);

  // T066: Handle loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center w-20 h-20 bg-surface-2 rounded animate-pulse">
        <span className="text-xs text-muted">Loading...</span>
      </div>
    );
  }

  // T067: Edit type badge label
  const getEditTypeBadge = (editType: string): string => {
    switch (editType) {
      case 'crop':
        return 'Crop';
      case 'light':
        return 'Light';
      case 'combined':
        return 'Crop + Light';
      default:
        return 'Edit';
    }
  };

  // Feature 652: Handle edit button click
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering thumbnail click
    if (onEdit) {
      onEdit();
    }
  };

  // Feature 652: Handle remove button click
  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering thumbnail click
    if (onRemove) {
      onRemove();
    }
  };

  return (
    <>
      {/* T064: Thumbnail (80x80px) */}
      <div className="group/container inline-block relative">
        <button
          type="button"
          onClick={() => setIsModalOpen(true)}
          className="relative block w-60 h-60 rounded overflow-hidden border-2 border-border hover:border-primary transition-colors cursor-pointer group"
          aria-label="View edit suggestion"
        >
          {/* Thumbnail preview */}
          <div className="w-full h-full">
            <EditPreviewRenderer
              id={photo.id}
              photoUrl={getVariantUrl(photo, modalVariant, true) || ''}
              photoTitle={photo.title}
              edit={edit}
              className="w-full h-full object-cover"
              loading="lazy"
              showDownloadButton={false}
            />
          </div>

          {/* T067: Edit type badge overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-primary/90 text-primary-foreground text-xs py-0.5 px-1 text-center font-medium group-hover:bg-primary transition-colors">
            {getEditTypeBadge(edit.editType)}
          </div>

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
              Click to view
            </span>
          </div>
        </button>

        {/* Feature 652: Hover-reveal action buttons */}
        {(onEdit || onRemove) && (
          <div className="absolute top-2 right-2 opacity-0 group-hover/container:opacity-100 transition-opacity duration-150 flex gap-1 z-10">
            {onEdit && (
              <button
                type="button"
                onClick={handleEditClick}
                className="p-1.5 bg-surface-1/90 hover:bg-surface-2 focus:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded border border-border shadow-sm transition-colors"
                aria-label="Edit edit suggestion"
              >
                <svg className="w-4 h-4 text-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
            {onRemove && (
              <button
                type="button"
                onClick={handleRemoveClick}
                className="p-1.5 bg-surface-1/90 hover:bg-danger/20 hover:text-danger focus:bg-danger/20 focus:text-danger focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-1 rounded border border-border shadow-sm transition-colors"
                aria-label="Remove edit suggestion"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* T065: Modal dialog with full-size preview */}
      <Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/80 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-[50%] top-[50%] z-50 h-[80vh] w-[80vw] translate-x-[-50%] translate-y-[-50%] flex flex-col rounded-lg border border-border bg-background p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <Dialog.Title className="text-lg font-semibold text-foreground">
                Suggested Edit: {getEditTypeBadge(edit.editType)}
              </Dialog.Title>
              <Dialog.Close className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="sr-only">Close</span>
              </Dialog.Close>
            </div>

            {/* Description */}
            <Dialog.Description className="text-sm text-muted mb-4 flex-shrink-0">
              Compare the original photo with the suggested edit. {edit.editType === 'crop' && 'The crop suggestion shows the recommended composition.'}
              {edit.editType === 'light' && 'The light adjustment suggestion shows recommended tonal improvements.'}
              {edit.editType === 'combined' && 'The suggestion includes both compositional (crop) and tonal (light) improvements.'}
            </Dialog.Description>

            {/* Comparison view - fills available space */}
            <div ref={handleRefAttached} className="flex-1 mb-4 overflow-hidden">
              <EditPreviewComparison
                id={photo.id}
                photoUrl={getVariantUrl(photo, modalVariant, true) || ''}
                photoTitle={photo.title}
                photo={photo}
                edit={edit}
              />
            </div>

            {/* Edit details */}
            <div className="p-4 bg-surface-1 rounded border border-border flex-shrink-0">
              <h4 className="text-sm font-medium text-foreground mb-2">Edit Parameters</h4>
              <pre className="text-xs text-muted overflow-x-auto">
                {JSON.stringify(edit.editData, null, 2)}
              </pre>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

export default EditSuggestionPreview;
